/**
 * @see mockMd/union_city_war_rank.md
 * 默认 VRT 对齐 tasks-docs/group-stage-rank/images/state-default.png
 * 可见：Top3×3 + 有奖行×3 + 无奖行×2 = list 8 条（首屏）
 * 行底板按 `can_get_reward` 区分（非 `is_promoted`）
 * `rank_reward_list` 对齐 reward-tier-drawer：区间 1 / 2,3 / 4,10；4 格必得、金币 8 位、门槛 100000
 * 分档半屏预览入参 `group=2`（`group=1` 走 Full List Mock）
 */
import {
  UnionCityWarPromoteType,
  UnionCityWarStage,
  type UnionCityWarRankData,
  type UnionCityWarRankItem,
  type UnionCityWarRankOwnItem,
  type UnionCityWarRankRewardItem,
  type UnionCityWarRewardItem,
} from '../types'
import storiesUnionAvatar from '../components/GroupStageRank/stories-images/stories-union-avatar.png'
import storiesZoneUnionAvatar from '../components/ZoneFullListDrawer/stories-images/stories-union-avatar.png'
import {storiesGiftIcon} from '../components/RewardTierDrawer/stories-images'

export const MOCK_USER_AVATAR = storiesUnionAvatar
export const MOCK_ZONE_FULL_LIST_AVATAR = storiesZoneUnionAvatar
export const MOCK_REWARD_TIER_ICON = storiesGiftIcon
export const MOCK_REWARD_DESC_NAME = 'Gift name*3d'

const mkItem = (
  rank: number,
  overrides: Partial<UnionCityWarRankItem> = {}
): UnionCityWarRankItem => ({
  rank,
  union_id: 10000 + rank,
  union_avatar: MOCK_USER_AVATAR,
  union_name: 'USER NAME US...',
  city_score: 300000,
  city_level: 90,
  coins: 1000000,
  is_promoted: UnionCityWarPromoteType.None,
  can_get_reward: false,
  ...overrides,
})

/** 首屏：rank1–3 Top3 · rank4–6 有预计奖励 · rank7–8 无预计奖励 */
const page1List: UnionCityWarRankItem[] = [
  mkItem(1, {
    union_id: 10001,
    is_promoted: UnionCityWarPromoteType.Both,
    can_get_reward: true,
    city_score: 300000,
    coins: 1000000,
  }),
  mkItem(2, {
    union_id: 10002,
    is_promoted: UnionCityWarPromoteType.Rank,
    can_get_reward: true,
  }),
  mkItem(3, {
    union_id: 10003,
    is_promoted: UnionCityWarPromoteType.Rank,
    can_get_reward: true,
  }),
  mkItem(99, {
    union_id: 10004,
    is_promoted: UnionCityWarPromoteType.Rank,
    can_get_reward: true,
  }),
  mkItem(100, {
    union_id: 10005,
    is_promoted: UnionCityWarPromoteType.Rank,
    can_get_reward: true,
  }),
  mkItem(101, {
    union_id: 10006,
    is_promoted: UnionCityWarPromoteType.LevelDirect,
    can_get_reward: true,
  }),
  mkItem(102, {
    union_id: 10007,
    is_promoted: UnionCityWarPromoteType.None,
    can_get_reward: false,
  }),
  mkItem(103, {
    union_id: 10008,
    is_promoted: UnionCityWarPromoteType.None,
    can_get_reward: false,
  }),
]

const ownDefault: UnionCityWarRankOwnItem = {
  ...mkItem(0, {
    union_id: 10086,
    union_name: 'USER NAME US...',
    is_promoted: UnionCityWarPromoteType.Rank,
    can_get_reward: true,
    city_score: 300000,
    coins: 1000000,
  }),
  prev_score_diff: 300000,
}

const rewardItem: UnionCityWarRewardItem = {
  id: 10001,
  name: 'Gift name',
  view_icon: MOCK_REWARD_TIER_ICON,
  toast_name: MOCK_REWARD_DESC_NAME,
  reward_type: 1,
  tag: 'must_1',
  quantity: 3,
  reward_value: 100,
  has_show_effect: true,
}

const rewardTierList: UnionCityWarRewardItem[] = [
  rewardItem,
  {...rewardItem, id: 10002},
  {...rewardItem, id: 10003},
  {...rewardItem, id: 10004},
]

/** 默认 VRT · 排行区间 1 / 2,3 / 4,10 */
const rankRewardListDefault: UnionCityWarRankRewardItem[] = [
  {
    rank_range: '1',
    threshold_a: 100000,
    threshold_b: 100000,
    threshold_c: 100000,
    coin_a: 10000000,
    coin_b: 10000000,
    coin_c: 10000000,
    reward_list: rewardTierList,
  },
  {
    rank_range: '2,3',
    threshold_a: 80000,
    threshold_b: 40000,
    threshold_c: 15000,
    coin_a: 8000000,
    coin_b: 4000000,
    coin_c: 1500000,
    reward_list: rewardTierList,
  },
  {
    rank_range: '4,10',
    threshold_a: 60000,
    threshold_b: 30000,
    threshold_c: 10000,
    coin_a: 6000000,
    coin_b: 3000000,
    coin_c: 1000000,
    reward_list: rewardTierList,
  },
]

/** 默认 VRT · page1 · more=true 便于触底演示 */
export const UnionCityWarRankMock: UnionCityWarRankData = {
  err_msg: '',
  stage: UnionCityWarStage.Group,
  group_no: 1,
  page: 1,
  more: true,
  countdown_sec: 5183999,
  promote_count: 10,
  direct_promote_level: 10,
  own_city_level: 90,
  own_city_score: 300000,
  direct_promote_score: 400000,
  rank_reward_list: rankRewardListDefault,
  list: page1List,
  topn: 10,
  own: ownDefault,
}

/** page2 · 续页无奖行 */
export const UnionCityWarRankPage2Mock: UnionCityWarRankData = {
  ...UnionCityWarRankMock,
  page: 2,
  more: false,
  list: [mkItem(9, {union_id: 10009}), mkItem(10, {union_id: 10010})],
  own: ownDefault,
}

/** 未开始 / 已结束 / 无数据 */
export const UnionCityWarRankEmptyMock: UnionCityWarRankData = {
  err_msg: '',
  stage: UnionCityWarStage.Promotion,
  group_no: 2,
  page: 1,
  more: false,
  countdown_sec: 0,
  promote_count: 5,
  direct_promote_level: 30,
  own_city_level: 0,
  own_city_score: 0,
  direct_promote_score: 800000,
  rank_reward_list: [],
  list: [],
  topn: 10,
  own: null,
}

/** 吸底变体：有奖不可晋级 */
export const UnionCityWarRankOwnRewardNoPromoteMock: UnionCityWarRankData = {
  ...UnionCityWarRankMock,
  own: {
    ...ownDefault,
    is_promoted: UnionCityWarPromoteType.None,
    can_get_reward: true,
  },
}

/** 吸底变体：无奖可晋级 */
export const UnionCityWarRankOwnPromoteNoRewardMock: UnionCityWarRankData = {
  ...UnionCityWarRankMock,
  own: {
    ...ownDefault,
    is_promoted: UnionCityWarPromoteType.Rank,
    can_get_reward: false,
  },
}

/** 吸底变体：无奖不可晋级 */
export const UnionCityWarRankOwnPlainMock: UnionCityWarRankData = {
  ...UnionCityWarRankMock,
  own: {
    ...ownDefault,
    is_promoted: UnionCityWarPromoteType.None,
    can_get_reward: false,
  },
}

/** 吸底变体：未上榜 / 已淘汰 · 对齐 state-sticky-unranked */
export const UnionCityWarRankOwnUnrankedMock: UnionCityWarRankData = {
  ...UnionCityWarRankMock,
  own: {
    ...ownDefault,
    rank: 0,
    is_promoted: UnionCityWarPromoteType.None,
    can_get_reward: false,
    prev_score_diff: 0,
  },
}

/** own 为空 · 不渲染吸底 */
export const UnionCityWarRankOwnNullMock: UnionCityWarRankData = {
  ...UnionCityWarRankMock,
  own: null,
}

const mkZoneFullListItem = (
  rank: number,
  overrides: Partial<UnionCityWarRankItem> = {}
): UnionCityWarRankItem => ({
  rank,
  union_id: 100000 + rank,
  union_avatar: MOCK_ZONE_FULL_LIST_AVATAR,
  union_name: 'USER NAME US...',
  city_score: 300000,
  city_level: 20,
  coins: 0,
  is_promoted: UnionCityWarPromoteType.None,
  can_get_reward: false,
  ...overrides,
})

/** 完整榜半屏 VRT · 6 条：1–4 有奖、5–6 无奖 99+ · group=1 */
export const UnionCityWarRankZoneFullListMock: UnionCityWarRankData = {
  err_msg: '',
  stage: UnionCityWarStage.Group,
  group_no: 1,
  page: 1,
  more: true,
  countdown_sec: 5183999,
  promote_count: 10,
  direct_promote_level: 20,
  own_city_level: 12,
  own_city_score: 300000,
  direct_promote_score: 400000,
  rank_reward_list: rankRewardListDefault,
  topn: 10,
  list: [
    mkZoneFullListItem(1, {
      can_get_reward: true,
      is_promoted: UnionCityWarPromoteType.Both,
    }),
    mkZoneFullListItem(2, {
      can_get_reward: true,
      is_promoted: UnionCityWarPromoteType.Rank,
    }),
    mkZoneFullListItem(3, {
      can_get_reward: true,
      is_promoted: UnionCityWarPromoteType.Rank,
    }),
    mkZoneFullListItem(4, {
      can_get_reward: true,
      is_promoted: UnionCityWarPromoteType.Rank,
    }),
    mkZoneFullListItem(99, {
      can_get_reward: false,
      is_promoted: UnionCityWarPromoteType.None,
    }),
    mkZoneFullListItem(100, {
      can_get_reward: false,
      is_promoted: UnionCityWarPromoteType.None,
    }),
  ],
  own: null,
}

/** 完整榜半屏续页 · 无奖行 · more=false */
export const UnionCityWarRankZoneFullListPage2Mock: UnionCityWarRankData = {
  ...UnionCityWarRankZoneFullListMock,
  page: 2,
  more: false,
  list: [
    mkZoneFullListItem(101, {
      can_get_reward: false,
      is_promoted: UnionCityWarPromoteType.None,
    }),
    mkZoneFullListItem(102, {
      can_get_reward: false,
      is_promoted: UnionCityWarPromoteType.None,
    }),
  ],
}
